// Per-project label cache. Loaded once when the TableView mounts for a
// project and refreshed manually if a future label-picker mutates it.

import { signal, type Signal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import type { LabelItem } from "@juuplan/shared";
import { api } from "../lib/api";

export interface LabelsState {
  loading: boolean;
  items: LabelItem[];
  projectId: number | null;
  error: string | null;
}

const initial: LabelsState = {
  loading: true,
  items: [],
  projectId: null,
  error: null,
};
const labelsSignal: Signal<LabelsState> = signal(initial);
let inFlight: Promise<void> | null = null;

async function loadLabels(projectId: number): Promise<void> {
  try {
    const items = await api<LabelItem[]>(`/api/projects/${projectId}/labels`);
    labelsSignal.value = { loading: false, items, projectId, error: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : "Unknown error";
    labelsSignal.value = {
      loading: false,
      items: [],
      projectId,
      error: message,
    };
  } finally {
    inFlight = null;
  }
}

export function useLabels(projectId: number): LabelsState {
  useEffect(() => {
    if (!inFlight && labelsSignal.value.projectId !== projectId) {
      // Reset synchronously so consumers never see the previous project's
      // items while the new project's labels are still loading.
      labelsSignal.value = {
        loading: true,
        items: [],
        projectId,
        error: null,
      };
      inFlight = loadLabels(projectId);
    }
  }, [projectId]);

  // Guard: only hand back items that actually belong to the requested
  // project. During a switch (e.g. another component triggered the load for a
  // different id) the cache may briefly point elsewhere — treat that as
  // loading rather than leaking foreign label chips.
  const state = labelsSignal.value;
  if (state.projectId !== projectId) {
    return { loading: true, items: [], projectId, error: null };
  }
  return state;
}

/** Build an id→LabelItem map for O(1) lookup inside row renderers. */
export function labelsById(items: LabelItem[]): Map<number, LabelItem> {
  const m = new Map<number, LabelItem>();
  for (const l of items) m.set(l.id, l);
  return m;
}

/** Optimistic add — call after creating a label via the API so the TableView
 * picks it up without a full refetch. */
export function addLabelToCache(projectId: number, label: LabelItem): void {
  const cur = labelsSignal.value;
  if (cur.projectId !== projectId) return;
  if (cur.items.some((l) => l.id === label.id)) return;
  labelsSignal.value = { ...cur, items: [...cur.items, label] };
}

/** Force re-fetch — used after schema-changing mutations. */
export function refreshLabels(projectId: number): Promise<void> {
  inFlight = loadLabels(projectId);
  return inFlight;
}
